import type { EmployeeRecord } from '../../team/store/useEmployeesStore';
import type { CashSessionRow } from '../infra/cashSessionRow';
import type { CashSummary } from '../infra/cashSummaryTypes';
import { CASH_OPERATOR_FILTER_ALL } from './cashList.constants';

/** Alineado a `CASHIER_LIST_FILTER_NULL` / RPC `__NULL_CASHIER__`. */
export const SALES_LIST_CASHIER_NULL_KEY = '__NULL_CASHIER__';

/** Operadores activos + dueño (sin scan de ventas ni RPC). */
export function collectCashOperatorIdsFromEmployees(
  employees: EmployeeRecord[],
  ownerAuthUserId: string | null | undefined
): string[] {
  const ids = new Set<string>();
  if (ownerAuthUserId) ids.add(ownerAuthUserId);
  for (const employee of employees) {
    if (employee.authUserId && employee.isActive !== false) {
      ids.add(employee.authUserId);
    }
  }
  return [...ids].sort();
}

/** Opciones del filtro Operador en Libro de ventas (empleados + Sin operador). */
export function buildSalesListCashierOptions(
  employees: EmployeeRecord[],
  ownerAuthUserId?: string | null,
  ownerEmail?: string | null
): Array<{ key: string; label: string }> {
  const ids = collectCashOperatorIdsFromEmployees(employees, ownerAuthUserId);
  const options = buildCashOperatorOptions(ids, employees, ownerAuthUserId, ownerEmail);
  return [...options, { key: SALES_LIST_CASHIER_NULL_KEY, label: 'Sin operador' }];
}

export function buildCashOperatorOptions(
  openerAuthUserIds: string[],
  employees: EmployeeRecord[],
  ownerAuthUserId?: string | null,
  ownerEmail?: string | null
): Array<{ key: string; label: string }> {
  const nameByAuth = new Map<string, string>();
  for (const employee of employees) {
    if (employee.authUserId) {
      nameByAuth.set(employee.authUserId, employee.name);
    }
  }

  return openerAuthUserIds.map((authUserId) => {
    // Nombre real primero: la membresía owner (selfOwner) viaja en `employees`
    // y trae full_name — mejor que el email para "quién vendió".
    const employeeName = nameByAuth.get(authUserId);
    if (employeeName) {
      return { key: authUserId, label: employeeName };
    }
    if (ownerAuthUserId && authUserId === ownerAuthUserId) {
      return { key: authUserId, label: ownerEmail?.trim() || 'Dueño' };
    }
    return { key: authUserId, label: `Operador ${authUserId.slice(0, 8)}…` };
  });
}

export function cashOperatorFilterLabel(
  operatorFilter: string,
  operatorOptions: Array<{ key: string; label: string }>
): string {
  if (operatorFilter === CASH_OPERATOR_FILTER_ALL) return 'Todos';
  return operatorOptions.find((o) => o.key === operatorFilter)?.label ?? 'Operador';
}

export function resolveCashOperatorLabel(
  authUserId: string | null | undefined,
  employees: EmployeeRecord[],
  ownerAuthUserId?: string | null,
  ownerEmail?: string | null
): string | null {
  if (!authUserId) return null;
  return buildCashOperatorOptions([authUserId], employees, ownerAuthUserId, ownerEmail)[0]?.label ?? null;
}

export function collectCashOperatorIdsFromSessions(
  history: CashSessionRow[],
  summary: CashSummary | null
): string[] {
  const ids = new Set<string>();
  for (const row of history) {
    if (row.openedBy) ids.add(row.openedBy);
    if (row.closedBy) ids.add(row.closedBy);
  }
  const active = summary?.activeSession;
  if (active?.openedBy) ids.add(active.openedBy);
  const last = summary?.lastClosedSession;
  if (last?.openedBy) ids.add(last.openedBy);
  if (last?.closedBy) ids.add(last.closedBy);
  return [...ids].sort();
}

export function buildCashHistoryOperatorOptions(
  currentBranchId: string | null | undefined,
  history: CashSessionRow[],
  summary: CashSummary | null,
  employees: EmployeeRecord[],
  ownerAuthUserId: string | null | undefined,
  ownerEmail: string | null | undefined
): Array<{ key: string; label: string }> {
  if (!currentBranchId) return [];
  const fromHistory = collectCashOperatorIdsFromSessions(history, summary);
  const ids =
    fromHistory.length > 0
      ? fromHistory
      : collectCashOperatorIdsFromEmployees(employees, ownerAuthUserId);
  return buildCashOperatorOptions(ids, employees, ownerAuthUserId, ownerEmail);
}
